import React from 'react';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { aboutData } from '../data/mock';
import { Target, Eye, Heart, Handshake, Shield, Award } from 'lucide-react';
import { SEO } from '../components/SEO';

const valueIcons = [Heart, Shield, Award, Handshake];

export default function AboutPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="About Us"
        path="/about"
        description="Learn about Naviton Global, an Australian international trade company based in Sydney, supporting import and export activities between Australia and global markets."
      />
      <Header />
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-20 bg-navy-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <span className="inline-block px-4 py-1.5 rounded-full bg-teal-500/20 text-teal-400 text-sm font-medium mb-6">
                About Us
              </span>
              <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
                Your Partner in
                <span className="block text-teal-400">Global Trade Excellence</span>
              </h1>
              <p className="text-xl text-slate-300 max-w-3xl mx-auto">
                {aboutData.overview}
              </p>
            </div>
          </div>
        </section>

        {/* Vision & Mission */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12">
              <div className="p-8 rounded-2xl bg-slate-50 border border-slate-200">
                <div className="p-3 w-fit rounded-xl bg-navy-900 mb-6">
                  <Eye className="h-8 w-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-navy-900 mb-4">Our Vision</h2>
                <p className="text-lg text-slate-600 leading-relaxed">
                  {aboutData.vision}
                </p>
              </div>
              <div className="p-8 rounded-2xl bg-slate-50 border border-slate-200">
                <div className="p-3 w-fit rounded-xl bg-teal-600 mb-6">
                  <Target className="h-8 w-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-navy-900 mb-4">Our Mission</h2>
                <p className="text-lg text-slate-600 leading-relaxed">
                  {aboutData.mission}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-navy-900 mb-4">Our Core Values</h2>
              <p className="text-xl text-slate-600">The principles that guide everything we do</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {aboutData.values.map((value, index) => {
                const Icon = valueIcons[index];
                return (
                  <div
                    key={value.title}
                    className="p-6 rounded-2xl bg-white border border-slate-200 hover:border-teal-300 hover:shadow-lg transition-all duration-300 text-center"
                  >
                    <div className="p-4 w-fit mx-auto rounded-xl bg-navy-900 mb-4">
                      <Icon className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-navy-900 mb-2">{value.title}</h3>
                    <p className="text-slate-600">{value.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Why Australia */}
        <section className="py-20 bg-navy-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-4xl font-bold text-white mb-6">
                  Why Australia as a
                  <span className="block text-teal-400">Sourcing Hub</span>
                </h2>
                <p className="text-xl text-slate-300 leading-relaxed mb-8">
                  {aboutData.whyAustralia}
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                    <div className="text-3xl font-bold text-teal-400 mb-1">20+</div>
                    <div className="text-slate-400 text-sm">Free Trade Agreements</div>
                  </div>
                  <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                    <div className="text-3xl font-bold text-teal-400 mb-1">#1</div>
                    <div className="text-slate-400 text-sm">Food Safety Standards</div>
                  </div>
                  <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                    <div className="text-3xl font-bold text-teal-400 mb-1">AAA</div>
                    <div className="text-slate-400 text-sm">Credit Rating</div>
                  </div>
                  <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                    <div className="text-3xl font-bold text-teal-400 mb-1">$450B</div>
                    <div className="text-slate-400 text-sm">Annual Exports</div>
                  </div>
                </div>
              </div>
              <div className="relative">
                <div className="rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://images.unsplash.com/photo-1523482580672-f109ba8cb9be?w=800&q=80" 
                    alt="Sydney Opera House and Harbour Bridge - Australia"
                    className="w-full h-full object-cover aspect-[4/3]"
                  />
                </div>
                {/* Decorative overlay badge */}
                <div className="absolute -bottom-6 -left-6 bg-teal-600 text-white px-6 py-4 rounded-xl shadow-lg">
                  <div className="text-2xl font-bold">Australia</div>
                  <div className="text-teal-100 text-sm">Gateway to Asia-Pacific</div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
